import { supabase } from './supabase'
import type { User } from '@supabase/supabase-js'
import { checkAdminPanelAccess } from '@/utils/roleCache'

export interface AuthError {
  message: string
  code?: string
}

export interface AuthResponse<T = unknown> {
  data?: T
  error?: AuthError
}

export const authService = {
  async signIn(email: string, password: string): Promise<AuthResponse<User>> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        return { error: { message: error.message, code: error.code } }
      }

      if (!data.user) {
        return { error: { message: 'No user returned from sign in' } }
      }

      // Check if user has admin panel access (super admin, admin, or marketer with blog permissions)
      const hasAccess = await checkAdminPanelAccess(supabase, data.user.id)

      if (!hasAccess) {
        await supabase.auth.signOut()
        return { error: { message: 'Access denied. Admin panel access required.' } }
      }

      return { data: data.user }
    } catch (error) {
      console.error('Sign in error:', error)
      return { error: { message: 'An unexpected error occurred' } }
    }
  },

  async signOut(): Promise<AuthResponse<void>> {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        return { error: { message: error.message } }
      }
      return { data: undefined }
    } catch (error) {
      console.error('Sign out error:', error)
      return { error: { message: 'An unexpected error occurred' } }
    }
  },

  async resetPassword(email: string): Promise<AuthResponse<void>> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) {
        return { error: { message: error.message } }
      }

      return { data: undefined }
    } catch (error) {
      console.error('Reset password error:', error)
      return { error: { message: 'An unexpected error occurred' } }
    }
  },

  async updatePassword(newPassword: string): Promise<AuthResponse<void>> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (error) {
        return { error: { message: error.message } }
      }

      return { data: undefined }
    } catch (error) {
      console.error('Update password error:', error)
      return { error: { message: 'An unexpected error occurred' } }
    }
  },

  async getSession() {
    const { data: { session } } = await supabase.auth.getSession()
    return session
  },

  async getUser() {
    const { data: { user } } = await supabase.auth.getUser()
    return user
  },

  onAuthStateChange(callback: (user: User | null) => void) {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      callback(session?.user ?? null)
    })
    return subscription
  }
}